import 'twin.macro';
import React from 'react';
import Popover from '@teetsh/app/src/components/Popover';
import { EyeIcon as Eye } from '@heroicons/react/24/outline';
import { getDurationInHoursAndMinutes } from '@teetsh/app/src/utils/time/getDurationInHoursAndMinutes';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { useTranslation } from 'react-i18next';
import useStandaloneFicheDePrep from 'queries/fdp/useStandaloneFicheDePrep';

interface Props {
  fdpId: string;
  detail?: string;
}

function DisplayList(props: {
  items: string[];
  singularLabel: string;
  pluralLabel: string;
}) {
  const items = props.items || [];

  if (items.length === 0) {
    return null;
  }

  const label = items.length > 1 ? props.pluralLabel : props.singularLabel;

  return (
    <div tw="flex gap-1 items-start text-sm text-neutral-moderate">
      <span tw="font-semibold shrink-0">{label}:</span>
      <span tw="flex-1">{items.join(', ')}</span>
    </div>
  );
}

export function FdpPreview(props: Props) {
  const { boLabels } = useUser();
  const { fdpId, detail } = props;
  const { t } = useTranslation(['sequence']);
  const { data } = useStandaloneFicheDePrep(fdpId);

  if (!data) {
    return <div tw="p-3">{t('sequence:loading')}</div>;
  }

  const totalDuration = (data.phases || []).reduce(
    (acc, phase) => phase.dureeInMinute + acc,
    0,
  );

  return (
    <div tw="flex flex-col gap-4 p-4">
      <div tw="flex flex-col gap-2">
        <div tw="flex items-center justify-between">
          <h4 tw="text-neutral-quiet">{t('sequence:generalInformation')}</h4>
          {detail && <span tw="text-sm text-neutral-moderate">{detail}</span>}
        </div>
        <div tw="flex flex-col gap-1">
          <DisplayList
            pluralLabel={boLabels.matierePluralName}
            singularLabel={boLabels.matiereSingularName}
            items={(data.bo?.matiere || []).map(m => m.value)}
          />
          <DisplayList
            pluralLabel={boLabels.domainePluralName}
            singularLabel={boLabels.domaineSingularName}
            items={(data.bo?.domaine || []).map(m => m.value)}
          />
          <DisplayList
            pluralLabel={boLabels.competencePluralName}
            singularLabel={boLabels.competenceSingularName}
            items={(data.bo?.competence || []).map(m => m.value)}
          />
          <DisplayList
            pluralLabel={boLabels.objectifPluralName}
            singularLabel={boLabels.objectifSingularName}
            items={(data.bo?.goals || []).map(m => m.value)}
          />
        </div>
      </div>

      {(data.phases || []).length > 0 && (
        <div tw="flex flex-col gap-2">
          <div tw="flex items-center justify-between">
            <h4 tw="text-neutral-quiet">{t('sequence:flow')}</h4>
            <span tw="text-sm text-neutral-moderate">
              {getDurationInHoursAndMinutes(totalDuration)}
            </span>
          </div>
          <div tw="flex flex-col gap-1">
            {data.phases.map((phase, index) => (
              <div
                key={phase.id}
                tw="flex items-center justify-between text-sm text-neutral-moderate"
              >
                <span tw="font-semibold">
                  {phase.title
                    ? phase.title.replace(/(<([^>]+)>)/gi, '')
                    : `${t('sequence:phases_one')} ${index + 1}`}
                </span>
                <span>{getDurationInHoursAndMinutes(phase.dureeInMinute)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function PreviewButton(props: Props) {
  const { fdpId, detail } = props;
  return (
    <Popover portal hover tw="block">
      <Popover.Button small minimal icon={Eye} tw="bg-neutral-quiet" />
      <Popover.Panel size="2xl">
        <FdpPreview fdpId={fdpId} detail={detail} />
      </Popover.Panel>
    </Popover>
  );
}
